import { env } from "@/lib/env";
import { ApiError } from "next/dist/server/api-utils";
import { toast } from "react-toastify";
import { ListingData } from "../../context/types";

const PROFILESENDPOINTURL = env.NEXT_PROFILES_API_URL;
const LISTINGSMANAGEMENTENDPOINTURL = env.NEXT_LISTINGS_MANAGEMENT_API_URL;

export const handleUploadListingVideo = async (
  videoFile: File,
  listingId: string,
  idToken: string | null,
  setField: <K extends keyof ListingData>(
    key: K,
    value: ListingData[K]
  ) => void,
  onProgress?: (percent: number | null) => void
) => {
  if (idToken === null) {
    toast("Please reauthenticate before performing this action.", {
      type: "error",
    });
    return;
  }
  try {
    const response = await fetch(
      `${PROFILESENDPOINTURL}/generateS3UploadLinks`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          image_keys: [videoFile.name],
          folder_id: listingId,
          root_folder_id: "listings-videos/",
          bucket_name: env.NEXT_PUBLIC_LISTINGS_VIDEOS_BUCKET_NAME,
        }),
      }
    );
    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }
    const urls = (await response.json()) as URLS;
    await uploadVideoXml(videoFile, urls, onProgress);
    const videoPresignedUrl = Object.values(urls.urls);
    const s3Urls = videoPresignedUrl.map((url) => extractVideoUrl(url));
    const videoS3Url = s3Urls[0];

    const databaseUploadResponse = await fetch(
      `${LISTINGSMANAGEMENTENDPOINTURL}/processListingVideo`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          listing_id: listingId,
          video_url: videoS3Url,
        }),
      }
    );

    if (databaseUploadResponse.ok) {
      const responseJson =
        (await databaseUploadResponse.json()) as DatabaseUploadResponse;
      setField("presentation_video_data", responseJson.video_data);
      if (onProgress) onProgress(null);
    } else {
      const apiErr = (await databaseUploadResponse.json()) as ApiError;
      throw new Error(apiErr.message);
    }
    return;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to upload your photos."
        : errorMessage,
      {
        type: "error",
      }
    );
    return;
  }
};

export const handleDeleteVideo = async (
  listingId: string,
  videoData: {
    video_url: string;
    thumbnail_url: string;
  },
  idToken: string | null,
  setField: <K extends keyof ListingData>(key: K, value: ListingData[K]) => void
) => {
  if (idToken === null) {
    toast("Please reauthenticate before performing this action.", {
      type: "error",
    });
    return;
  }
  try {
    setField("presentation_video_data", {
      video_url: "",
      thumbnail_url: "",
    });
    const response = await fetch(
      `${LISTINGSMANAGEMENTENDPOINTURL}/deletePendingVideoDynamoDB`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          listing_id: listingId,
          delete_key: videoData.video_url,
        }),
      }
    );
    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }
  } catch (err) {
    setField("presentation_video_data", videoData);
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to delete video."
        : errorMessage,
      {
        type: "error",
      }
    );
    return;
  }
};

const uploadVideoXml = async (
  videoFile: File,
  urls: URLS,
  onProgress?: (percent: number) => void
) => {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const uploadUrl = urls.urls[videoFile.name];

    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("Content-Type", videoFile.type);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        if (percent < 100) {
          onProgress(percent);
        }
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(uploadUrl);
      } else {
        reject(new Error(`Upload failed for ${videoFile.name}`));
      }
    };

    xhr.onerror = () =>
      reject(new Error(`Upload failed for ${videoFile.name}`));

    xhr.send(videoFile);
  });
};

const uploadVideo = async (videoFile: File, urls: URLS) => {
  const xhr = new XMLHttpRequest();
  const uploadResponse = await fetch(urls.urls[videoFile.name], {
    method: "PUT",
    headers: {
      "Content-Type": videoFile.type,
    },
    body: videoFile,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed for ${videoFile.name}`);
  }

  return urls.urls[videoFile.name];
};

interface URLS {
  urls: { [key: string]: string };
}

const extractVideoUrl = (url: string): string | null => {
  const baseUrlRegex = /^(https:\/\/.*?\/.*?\/.*?\.(mp4|mov))/i;
  const match = url.match(baseUrlRegex);
  return match ? match[0] : null;
};

interface DatabaseUploadResponse {
  video_data: {
    video_url: string;
    thumbnail_url: string;
  };
}
